import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { Image, Upload, Spin } from 'antd';
import { DEFAULT_SIZE, ProfilePictureContainer } from './styles';
import Notification from 'components/ui/base/notification/Notification';

const ACCEPTED_IMAGE_TYPES = 'image/png,image/jpeg,image/svg+xml,image/gif';

const isAcceptedImageType = (file: File) => {
    return ['image/png', 'image/jpeg', 'image/svg+xml', 'image/gif'].includes(file.type);
};

export type ProfilePictureRef = {
    openFileDialog: () => void;
};

export type PictureState = {
    uploading: boolean;
    deleting: boolean;
};

type Props = {
    onUpload?: (file: File) => void | Promise<void>;
    onDelete?: () => void | Promise<void>;
    onPictureStateChange?: (state: PictureState) => void;
    profilePicture: string | null | undefined;
    viewOnly?: boolean;
    style?: React.CSSProperties;
    size?: number;
    loading?: boolean;
};

const ProfilePicture = forwardRef<ProfilePictureRef, Props>((props, ref) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const [pictureStatus, setPictureStatus] = useState<{
        loading: boolean;
        action: 'none' | 'uploading' | 'deleting';
    }>({ loading: false, action: 'none' });

    const [picturePreview, setPicturePreview] = useState<{
        visible: boolean;
        preview: string | null;
    }>({ visible: false, preview: null });

    const isBusy = props.loading || pictureStatus.loading;

    const uploadButton = (
        <button style={{ border: 0, background: 'none' }} type="button">
            {pictureStatus.loading ? <LoadingOutlined /> : <PlusOutlined />}
            <div style={{ marginTop: 8 }}>Upload</div>
        </button>
    );

    const handleUpload = async (file: File) => {
        setPictureStatus({ loading: true, action: 'uploading' });
        try {
            await props.onUpload?.(file);
        } catch (e: any) {
            throw e;
        } finally {
            setPictureStatus({ loading: false, action: 'none' });
        }
    };

    const handleDelete = async () => {
        setPictureStatus({ loading: true, action: 'deleting' });
        try {
            await props.onDelete?.();
        } catch (e: any) {
        } finally {
            setPictureStatus({ loading: false, action: 'none' });
        }
    };

    useImperativeHandle(ref, () => ({
        openFileDialog: () => {
            if (props.viewOnly) return;
            fileInputRef.current?.click();
        },
    }));

    const handleHiddenInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        // allow selecting same file again later
        e.target.value = '';

        if (!isAcceptedImageType(file)) {
            Notification({
                type: 'error',
                messageTitle: 'Invalid file type',
                description: 'Please provide a file in one of the following formats: JPEG, JPG, SVG, PNG, GIF.',
                placement: 'top',
            });
            return;
        }

        await handleUpload(file);
    };

    const handlePreview = async (file: any) => {
        const getBase64 = (file: any) =>
            new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = (error) => reject(error);
            });

        if (!file.url && !file.preview) {
            // eslint-disable-next-line no-param-reassign
            file.preview = await getBase64(file.originFileObj);
        }

        setPicturePreview({ visible: true, preview: file.url || file.preview });
    };

    useEffect(() => {
        props.onPictureStateChange?.({
            uploading: pictureStatus.action === 'uploading' && pictureStatus.loading,
            deleting: pictureStatus.action === 'deleting' && pictureStatus.loading,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pictureStatus]);

    return (
        <ProfilePictureContainer $size={props.size} $hideUpload={Boolean(props.profilePicture)}>
            {/* Hidden file input controlled by external button */}
            <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_IMAGE_TYPES}
                hidden
                onChange={handleHiddenInputChange}
            />

            {isBusy ? (
                <div
                    style={{
                        width: props.size ?? DEFAULT_SIZE,
                        height: props.size ?? DEFAULT_SIZE,
                        border: '1px dashed rgba(0,0,0,0.2)',
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                </div>
            ) : (
                <Upload
                    name="avatar"
                    listType="picture-card"
                    accept={ACCEPTED_IMAGE_TYPES}
                    fileList={
                        props.profilePicture ? [{ uid: '1', name: '', status: 'done', url: props.profilePicture }] : []
                    }
                    disabled={props.viewOnly}
                    beforeUpload={async (file) => {
                        const f = file as File;

                        if (!isAcceptedImageType(f)) {
                            Notification({
                                type: 'error',
                                messageTitle: 'Invalid file type',
                                description:
                                    'Please provide a file in one of the following formats: JPEG, JPG, SVG, PNG, GIF.',
                                placement: 'top',
                            });

                            return false;
                        }

                        await handleUpload(f);
                    }}
                    onRemove={async () => {
                        await handleDelete();
                        return true;
                    }}
                    onPreview={handlePreview}
                >
                    {props.profilePicture ? null : uploadButton}
                </Upload>
            )}

            {picturePreview.preview && (
                <Image
                    wrapperStyle={{ display: 'none' }}
                    preview={{
                        visible: picturePreview.visible,
                        onVisibleChange: (visible) => setPicturePreview((prev) => ({ ...prev, visible })),
                    }}
                    alt="Profile Picture"
                    src={picturePreview.preview}
                />
            )}
        </ProfilePictureContainer>
    );
});

export default ProfilePicture;
